<template>
    <!--
      3D 视差倾斜卡片：鼠标在卡片上移动时，卡片绕 X/Y 轴轻微旋转，
      并有一束光斑跟随指针，营造"实体悬浮玻璃"质感。
      通过 disabled 可在移动端关闭倾斜（触控下倾斜反而别扭）。
    -->
    <div ref="el" class="tilt card neon" :class="{ 'is-active': active }" :style="style" @mousemove="onMove"
        @mouseenter="active = true" @mouseleave="reset">
        <div class="tilt-glare" :style="glareStyle"></div>
        <div class="tilt-inner">
            <slot />
        </div>
    </div>
</template>

<script setup>
import { computed, reactive, ref } from "vue";

const props = defineProps({
    // 最大倾斜角度（度）
    max: { type: Number, default: 8 },
    // 移动端可传 true 关闭倾斜
    disabled: { type: Boolean, default: false },
});

const el = ref(null);
const active = ref(false);

// rx/ry 旋转角，gx/gy 光斑位置百分比
const t = reactive({ rx: 0, ry: 0, gx: 50, gy: 50 });

function onMove(e) {
    if (props.disabled) return;
    // 路由切换的离场过渡期间，组件正在卸载，el 可能已被置空。
    // 此时鼠标移动事件仍可能命中正在淡出的卡片，加一层守卫避免读取 null。
    if (!el.value) return;
    const rect = el.value.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width; // 0~1
    const py = (e.clientY - rect.top) / rect.height; // 0~1

    // 以中心为原点换算成倾斜角：右侧抬起、上方抬起
    t.ry = (px - 0.5) * props.max * 2;
    t.rx = -(py - 0.5) * props.max * 2;
    t.gx = px * 100;
    t.gy = py * 100;
}

function reset() {
    active.value = false;
    t.rx = 0;
    t.ry = 0;
    t.gx = 50;
    t.gy = 50;
}

const style = computed(() => ({
    transform: `perspective(900px) rotateX(${t.rx}deg) rotateY(${t.ry}deg)`,
}));

const glareStyle = computed(() => ({
    background: `radial-gradient(420px circle at ${t.gx}% ${t.gy}%, rgba(255,255,255,.14), transparent 60%)`,
}));
</script>

<style scoped>
.tilt {
    position: relative;
    transform-style: preserve-3d;
    transition: transform var(--t) cubic-bezier(.22, 1, .36, 1), box-shadow var(--t);
    will-change: transform;
}

.tilt.is-active {
    box-shadow: var(--shadow), var(--glow-violet);
    z-index: 2;
}

.tilt-glare {
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    opacity: 0;
    transition: opacity var(--t);
    mix-blend-mode: screen;
}

.tilt.is-active .tilt-glare {
    opacity: 1;
}

.tilt-inner {
    position: relative;
    height: 100%;
    /* 内容稍微抬离卡面，强化 3D 层次 */
    transform: translateZ(20px);
}
</style>
